import { type KeyStatSurveyItem, type ReportOption, type ReportWithComments } from '../../../models/Report'
import { SurveyQuestionType } from '../../../models/Survey'

export interface QuestionSampleStats {
  label: string
  n: number
  totalResponses: number
  naCount: number
}

export const formatSampleCount = (value: number): string => value.toLocaleString()

const NOT_APPLICABLE_LABELS = new Set([
  'n/a',
  'na',
  'not applicable'
])

const normalizeLabel = (value?: string): string =>
  value?.trim().toLowerCase().replace(/\s+/g, ' ') ?? ''

const toSafeCount = (value: number | null | undefined): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0

const getSeriesLabel = (titles: string[], index: number): string => titles[index] ?? `Series ${index + 1}`

const hasOnlyNotApplicableWeights = (option: ReportOption): boolean => {
  if (!option.weights?.length) {
    return false
  }

  return option.weights.every(weight => weight == null || weight === 0)
}

export const isNotApplicableOption = (
  option: Pick<ReportOption, 'name' | 'weights'>,
  itemType?: SurveyQuestionType
): boolean => {
  if (NOT_APPLICABLE_LABELS.has(normalizeLabel(option.name))) {
    return true
  }

  return itemType === SurveyQuestionType.LinearScale && hasOnlyNotApplicableWeights(option as ReportOption)
}

export const getChartDisplayOptions = (
  report?: KeyStatSurveyItem | null
): ReportOption[] => {
  if (!report?.options?.length) {
    return []
  }

  return report.options.filter(option => !isNotApplicableOption(option, report.itemType))
}

export const getQuestionSampleStats = ({
  report,
  titles
}: {
  report: KeyStatSurveyItem | ReportWithComments | null | undefined
  titles?: string[]
}): QuestionSampleStats[] | null => {
  if (!report) {
    return null
  }

  const seriesTitles = titles ?? []

  if (report.itemType === SurveyQuestionType.MultiSelect) {
    return null
  }

  if (report.itemType === SurveyQuestionType.FreeResponse && 'comments' in report) {
    const commentBuckets = report.comments ?? []
    const seriesCount = Math.max(seriesTitles.length, commentBuckets.length)

    if (seriesCount === 0) {
      return null
    }

    return Array.from({ length: seriesCount }, (_, index) => {
      const commentCount = (commentBuckets[index] ?? []).filter(
        (comment: string | null | undefined): comment is string =>
          typeof comment === 'string' && comment.trim().length > 0
      ).length

      return {
        label: getSeriesLabel(seriesTitles, index),
        n: commentCount,
        totalResponses: commentCount,
        naCount: 0
      }
    })
  }

  const options = 'options' in report ? report.options ?? [] : []
  const seriesCount = Math.max(
    seriesTitles.length,
    ...options.map(option => option.counts?.length ?? 0),
    0
  )

  if (
    seriesCount === 0 ||
    (report.itemType !== SurveyQuestionType.LinearScale &&
      report.itemType !== SurveyQuestionType.MultipleChoice)
  ) {
    return null
  }

  const naOptions = options.filter(option => isNotApplicableOption(option, report.itemType))

  return Array.from({ length: seriesCount }, (_, index) => {
    const totalResponses = options.reduce((sum, option) => sum + toSafeCount(option.counts?.[index]), 0)
    const naCount = naOptions.reduce((sum, option) => sum + toSafeCount(option.counts?.[index]), 0)

    return {
      label: getSeriesLabel(seriesTitles, index),
      n: Math.max(0, totalResponses - naCount),
      totalResponses,
      naCount
    }
  })
}

export const formatQuestionSampleStat = (stat: QuestionSampleStats): string =>
  stat.naCount > 0
    ? `N: ${formatSampleCount(stat.n)} (out of ${formatSampleCount(stat.totalResponses)} | ${formatSampleCount(stat.naCount)} N/A)`
    : `N: ${formatSampleCount(stat.n)} (out of ${formatSampleCount(stat.totalResponses)})`
